/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Box, Flex, useBreakpointIndex, Button } from '@tishman/components';
import React, { useMemo } from 'react';

import {
  actions,
  selectFlow,
  selectShopCartSelections,
  selectTotalQuantity,
  useAppDispatch,
  useAppSelector,
} from '../../../store';
import {
  Flow,
  StatGroup,
  ProductOrderByFlow,
} from '../../../services/viva/constants';
import { NewTicketLine } from '../NewTicketLine';
import { formatMoney } from '../../../utils';
import { Translatable, useTranslations } from '../../../utils/use-translations';

import type { TicketQuantityProps } from './NewTicketQuantityTypes';
import type { TicketTypesForFlow } from '../../../data/translations/ticketType';

const SkipBookingButton = ({
  skipDisabled,
  onSkip,
}: {
  skipDisabled: boolean;
  onSkip: () => void;
}) => (
  <Button
    disabled={skipDisabled}
    onClick={onSkip}
    sx={{
      'alignSelf': 'flex-start',
      'mt': 3,
      'order': 2,
      'width': 'fit-content',
      'transition': 'opacity 0.25s ease-in-out',
      '&:disabled': {
        opacity: 0.3,
        pointerEvents: 'none',
      },
    }}
    variant="underline"
  >
    Skip Booking &amp; Purchase Now
  </Button>
);

export const NewTicketQuantity = ({
  description,
  hideLabel,
  ticketTypes,
  continueButton,
  continueMessage,
}: TicketQuantityProps) => {
  /**
   * Hooks
   */
  const dispatch = useAppDispatch();
  const flow = useAppSelector(selectFlow);
  const selections = useAppSelector(selectShopCartSelections);
  const totalTickets = useAppSelector(selectTotalQuantity);
  const translations = useTranslations<TicketTypesForFlow>(
    Translatable.TicketType,
  );

  const skipDisabled = !selections || selections.length === 0;
  const skipBooking = [Flow.CITY_PASS, Flow.C3].includes(flow!);
  const isMobile = useBreakpointIndex() < 2;
  const _ticketTypes = ticketTypes
    ? ticketTypes.filter(
        (ticketType): ticketType is NonNullable<typeof ticketType> =>
          Boolean(ticketType),
      )
    : [];

  const translatedTicketTypes = useMemo(() => {
    const types = ticketTypes
      ? _ticketTypes.map((ticketType) => {
          const lastIndex = ticketType.statGroup.length - 1;
          const statGroupTranslation =
            translations?.byStatGroup[
              ticketType.statGroup[lastIndex] as StatGroup
            ];
          return {
            ...ticketType,
            label: statGroupTranslation?.label ?? ticketType.label ?? '',
            helpLabel:
              statGroupTranslation?.helpLabel ?? ticketType.helpLabel ?? '',
          };
        })
      : [];

    // If flow exists in ProductOrderByFlow, sort based on that order
    if (flow && ProductOrderByFlow[flow]) {
      const orderMap = new Map(
        ProductOrderByFlow[flow]!.map((statGroup: StatGroup, index: number) => [
          statGroup,
          index,
        ]),
      );

      return types.sort((a, b) => {
        const lastIndexA = a.statGroup.length - 1;
        const lastIndexB = b.statGroup.length - 1;
        const statGroupA = a.statGroup[lastIndexA] as StatGroup;
        const statGroupB = b.statGroup[lastIndexB] as StatGroup;

        const orderA = orderMap.get(statGroupA);
        const orderB = orderMap.get(statGroupB);

        // If both statGroups are in the order map, sort by their position
        if (orderA !== undefined && orderB !== undefined) {
          return orderA - orderB;
        }
        // If only one is in the order map, put it first
        if (orderA !== undefined) return -1;
        if (orderB !== undefined) return 1;
        // If neither is in the order map, maintain original order
        return 0;
      });
    }

    return types;
  }, [ticketTypes, translations?.byStatGroup, flow]);

  /*
   * Handlers
   */
  const onSkip = () => {
    dispatch(actions.order.setShopCartDateTime(0));
    dispatch(actions.order.setSkipToBilling(true));
  };

  return (
    <Flex
      sx={{
        justifyContent: 'space-between',
        flexDirection: ['column', 'column', 'column', 'row'],
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 470 }}>{description}</Box>
      {/* Tickets Table */}
      <Flex
        sx={{
          mt: [4, 4, 4, -5],
          flexDirection: 'column',
          order: [0, 0, 1],
          maxWidth: ['none', 'none', 'none', '490px'],
        }}
      >
        {/* TICKET QUANTITY SELECTION */}
        <Box sx={{ mb: '16px' }}>
          {translatedTicketTypes.map(
            ({
              label,
              helpLabel,
              price,
              tax,
              pricetableAK,
              priceRange,
              statGroup,
              productAK,
              type,
            }) => (
              <NewTicketLine
                helpLabel={helpLabel}
                hideLabel={hideLabel}
                key={productAK!}
                label={label!}
                price={price}
                priceRange={priceRange}
                pricetableAK={pricetableAK}
                productAK={productAK!}
                statGroup={statGroup}
                tax={tax}
                type={type}
              />
            ),
          )}
        </Box>
        {!isMobile && continueButton}
        {!isMobile && continueMessage}
        {skipBooking && !isMobile && (
          <SkipBookingButton onSkip={onSkip} skipDisabled={skipDisabled} />
        )}
      </Flex>
      {isMobile && continueButton}
      {isMobile && skipBooking && (
        <SkipBookingButton onSkip={onSkip} skipDisabled={skipDisabled} />
      )}
      {isMobile && continueMessage}

      <span aria-live="assertive" className="sr-only" role="status">
        {`Ticket Quantity ${totalTickets} and total is ${formatMoney(
          selections.reduce((acc, curr) => acc + curr.count * curr.price, 0),
        )}`}
      </span>
    </Flex>
  );
};
